import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, RefreshCw, Settings, ToggleLeft, ToggleRight, Edit, Save,
  MessageCircle, FileText, Star, QrCode, Link2, Calculator, CreditCard,
  Eye, EyeOff, TrendingUp, Users, Download, Trash2, Plus, BarChart3, Wrench
} from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';

const defaultTools = [
  {
    id: 'whatsapp-link',
    name: 'WhatsApp Link Generator',
    description: 'Create shareable WhatsApp links with pre-filled messages',
    icon: 'MessageCircle',
    color: 'from-green-500 to-green-600',
    path: '/tools/whatsapp-link',
    is_enabled: true,
    is_premium: false,
    daily_limit: 0,
    order: 1
  },
  {
    id: 'invoice',
    name: 'Invoice Generator',
    description: 'Create professional invoices and bills with GST',
    icon: 'FileText',
    color: 'from-blue-500 to-blue-600',
    path: '/tools/invoice',
    is_enabled: true,
    is_premium: false,
    daily_limit: 0,
    order: 2
  },
  {
    id: 'testimonials',
    name: 'Testimonial Collector',
    description: 'Collect and display customer reviews beautifully',
    icon: 'Star',
    color: 'from-yellow-500 to-orange-500',
    path: '/tools/testimonials',
    is_enabled: true,
    is_premium: false,
    daily_limit: 0,
    order: 3
  },
  {
    id: 'qr-code',
    name: 'QR Code Generator',
    description: 'Generate QR codes for any text, URL, or contact',
    icon: 'QrCode',
    color: 'from-purple-500 to-pink-500',
    path: '/tools/qr-code',
    is_enabled: true,
    is_premium: false,
    daily_limit: 0,
    order: 4
  },
  {
    id: 'bio-link',
    name: 'Bio Link Generator',
    description: 'Create Instagram-style link-in-bio pages',
    icon: 'Link2',
    color: 'from-pink-500 to-purple-500',
    path: '/tools/bio-link',
    is_enabled: true,
    is_premium: false,
    daily_limit: 0,
    order: 5
  },
  {
    id: 'emi-calculator',
    name: 'EMI Calculator',
    description: 'Calculate loan EMIs and payment schedules instantly',
    icon: 'Calculator',
    color: 'from-emerald-500 to-teal-500',
    path: '/tools/emi-calculator',
    is_enabled: true,
    is_premium: false,
    daily_limit: 0,
    order: 6
  },
  {
    id: 'business-card',
    name: 'Digital Business Card',
    description: 'Create shareable digital business cards with QR',
    icon: 'CreditCard',
    color: 'from-amber-500 to-orange-500',
    path: '/tools/business-card',
    is_enabled: true,
    is_premium: false,
    daily_limit: 0,
    order: 7
  }
];

const iconMap = {
  MessageCircle, FileText, Star, QrCode, Link2, Calculator, CreditCard
};

const FreeToolsAdmin = () => {
  const [loading, setLoading] = useState(true);
  const [tools, setTools] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [editingTool, setEditingTool] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newTool, setNewTool] = useState({
    id: '', name: '', description: '', icon: 'Wrench', 
    color: 'from-gray-500 to-gray-600', path: '', is_enabled: true, is_premium: false, daily_limit: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Try to get tools config from backend, otherwise use defaults
      const [toolsRes, analyticsRes] = await Promise.all([
        api.get('/admin/free-tools').catch(() => ({ data: null })),
        api.get('/analytics/tools/summary').catch(() => ({ data: null }))
      ]);
      
      if (toolsRes.data && toolsRes.data.length > 0) {
        setTools(toolsRes.data);
      } else {
        setTools(defaultTools);
      }
      
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error('Failed to fetch tools:', error);
      setTools(defaultTools);
    } finally {
      setLoading(false);
    }
  };

  const toggleTool = async (toolId) => {
    const updatedTools = tools.map(t => 
      t.id === toolId ? { ...t, is_enabled: !t.is_enabled } : t
    );
    setTools(updatedTools);
    
    try {
      await api.put(`/admin/free-tools/${toolId}`, { 
        is_enabled: !tools.find(t => t.id === toolId).is_enabled 
      });
      toast.success('Tool updated!');
    } catch (error) {
      toast.error('Failed to update tool');
      fetchData(); // Revert on error
    }
  };

  const togglePremium = async (toolId) => {
    const updatedTools = tools.map(t => 
      t.id === toolId ? { ...t, is_premium: !t.is_premium } : t
    );
    setTools(updatedTools);
    
    try {
      await api.put(`/admin/free-tools/${toolId}`, { 
        is_premium: !tools.find(t => t.id === toolId).is_premium 
      });
      toast.success('Tool updated!');
    } catch (error) {
      toast.error('Failed to update tool');
      fetchData();
    }
  };

  const updateToolLimit = async (toolId, limit) => {
    try {
      await api.put(`/admin/free-tools/${toolId}`, { daily_limit: parseInt(limit) || 0 });
      toast.success('Limit updated!');
      setEditingTool(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update limit');
    }
  };

  const saveTool = async (tool) => {
    try {
      await api.put(`/admin/free-tools/${tool.id}`, tool);
      toast.success('Tool saved!');
      setEditingTool(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to save tool');
    }
  };

  const addTool = async () => {
    if (!newTool.id || !newTool.name) {
      toast.error('ID and Name are required');
      return;
    }
    
    try {
      await api.post('/admin/free-tools', {
        ...newTool,
        order: tools.length + 1
      });
      toast.success('Tool added!');
      setShowAddModal(false);
      setNewTool({
        id: '', name: '', description: '', icon: 'Wrench', 
        color: 'from-gray-500 to-gray-600', path: '', is_enabled: true, is_premium: false, daily_limit: 0
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to add tool');
    }
  };

  const deleteTool = async (toolId) => {
    if (!window.confirm('Delete this tool?')) return;
    
    try {
      await api.delete(`/admin/free-tools/${toolId}`);
      toast.success('Tool deleted!');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete tool');
    }
  };

  const getToolStats = (toolId) => {
    if (!analytics?.tools) return { views: 0, generates: 0 };
    const tool = analytics.tools.find(t => t.tool_name === toolId);
    return {
      views: tool?.views || 0,
      generates: tool?.generates || 0
    };
  };

  const IconComponent = ({ iconName }) => {
    const Icon = iconMap[iconName] || Wrench;
    return <Icon className="w-6 h-6 text-white" />;
  };

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="free-tools-admin">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <Link to="/admin" className="text-muted-foreground hover:text-white text-sm flex items-center gap-1 mb-2">
              <ArrowLeft className="w-4 h-4" /> Back to Admin
            </Link>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <Wrench className="w-8 h-8 text-cyan-500" />
              Free Tools Management
            </h1>
            <p className="text-muted-foreground mt-1">Enable, disable, and configure free tools</p>
          </div>
          <div className="flex gap-2">
            <button 
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2 bg-cyan-500 text-white rounded-xl flex items-center gap-2 hover:bg-cyan-600"
            >
              <Plus className="w-4 h-4" /> Add Tool
            </button>
            <button onClick={fetchData} className="p-3 rounded-xl bg-white/10 hover:bg-white/20">
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Summary Stats */}
        {analytics && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="glass-card p-5">
              <Wrench className="w-7 h-7 text-cyan-500 mb-2" />
              <p className="text-2xl font-bold">{tools.length}</p>
              <p className="text-sm text-muted-foreground">Total Tools</p>
            </div>
            <div className="glass-card p-5">
              <Eye className="w-7 h-7 text-blue-500 mb-2" />
              <p className="text-2xl font-bold">{tools.filter(t => t.is_enabled).length}</p>
              <p className="text-sm text-muted-foreground">Active Tools</p>
            </div>
            <div className="glass-card p-5">
              <TrendingUp className="w-7 h-7 text-green-500 mb-2" />
              <p className="text-2xl font-bold">{analytics?.summary?.total_views || 0}</p>
              <p className="text-sm text-muted-foreground">Total Views</p>
            </div>
            <div className="glass-card p-5">
              <Download className="w-7 h-7 text-purple-500 mb-2" />
              <p className="text-2xl font-bold">{analytics?.summary?.total_generates || 0}</p>
              <p className="text-sm text-muted-foreground">Total Generates</p>
            </div>
          </div>
        )}

        {/* Tools List */}
        <div className="glass-card">
          <div className="p-4 border-b border-white/10 flex items-center justify-between">
            <h2 className="font-bold text-xl">All Free Tools ({tools.length})</h2>
            <Link to="/admin/tools-analytics" className="text-sm text-cyan-500 hover:underline flex items-center gap-1">
              <BarChart3 className="w-4 h-4" /> View Analytics
            </Link>
          </div>
          
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500 mx-auto"></div>
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {tools.map((tool) => {
                const stats = getToolStats(tool.id);
                return (
                  <div key={tool.id} className={`p-4 hover:bg-white/5 transition-colors ${!tool.is_enabled ? 'opacity-50' : ''}`}>
                    <div className="flex items-center gap-4">
                      {/* Icon */}
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${tool.color} flex items-center justify-center flex-shrink-0`}>
                        <IconComponent iconName={tool.icon} />
                      </div>
                      
                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-bold">{tool.name}</h3>
                          {tool.is_premium && (
                            <span className="text-xs bg-amber-500/20 text-amber-500 px-2 py-0.5 rounded-full">PREMIUM</span>
                          )}
                          {!tool.is_enabled && (
                            <span className="text-xs bg-red-500/20 text-red-500 px-2 py-0.5 rounded-full">DISABLED</span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground truncate">{tool.description}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Path: <code className="bg-white/10 px-1 rounded">{tool.path}</code>
                          {tool.daily_limit > 0 && (
                            <span className="ml-2">• Limit: {tool.daily_limit}/day</span>
                          )}
                        </p>
                      </div>
                      
                      {/* Stats */}
                      <div className="hidden md:flex items-center gap-6 text-sm">
                        <div className="text-center">
                          <p className="font-bold text-blue-500">{stats.views}</p>
                          <p className="text-xs text-muted-foreground">Views</p>
                        </div>
                        <div className="text-center">
                          <p className="font-bold text-green-500">{stats.generates}</p>
                          <p className="text-xs text-muted-foreground">Generates</p>
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        {/* Enable/Disable Toggle */}
                        <button
                          onClick={() => toggleTool(tool.id)}
                          className={`p-2 rounded-lg transition-colors ${
                            tool.is_enabled 
                              ? 'bg-green-500/20 text-green-500 hover:bg-green-500/30' 
                              : 'bg-red-500/20 text-red-500 hover:bg-red-500/30'
                          }`}
                          title={tool.is_enabled ? 'Disable Tool' : 'Enable Tool'}
                        >
                          {tool.is_enabled ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                        </button>
                        
                        {/* Premium Toggle */}
                        <button
                          onClick={() => togglePremium(tool.id)}
                          className={`p-2 rounded-lg transition-colors ${
                            tool.is_premium 
                              ? 'bg-amber-500/20 text-amber-500 hover:bg-amber-500/30' 
                              : 'bg-white/10 text-muted-foreground hover:bg-white/20'
                          }`}
                          title={tool.is_premium ? 'Make Free' : 'Make Premium'}
                        >
                          <Star className="w-5 h-5" />
                        </button>
                        
                        {/* Edit */}
                        <button
                          onClick={() => setEditingTool(tool)}
                          className="p-2 rounded-lg bg-blue-500/20 text-blue-500 hover:bg-blue-500/30"
                          title="Edit Tool"
                        >
                          <Edit className="w-5 h-5" />
                        </button>
                        
                        {/* Delete */}
                        <button
                          onClick={() => deleteTool(tool.id)}
                          className="p-2 rounded-lg bg-red-500/20 text-red-500 hover:bg-red-500/30"
                          title="Delete Tool"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Legend */}
        <div className="mt-6 glass-card p-4">
          <h3 className="font-semibold mb-3">Quick Actions</h3>
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2">
              <ToggleRight className="w-4 h-4 text-green-500" />
              <span>Enable/Disable tool visibility</span>
            </div>
            <div className="flex items-center gap-2">
              <Star className="w-4 h-4 text-amber-500" />
              <span>Toggle premium status</span>
            </div>
            <div className="flex items-center gap-2">
              <Edit className="w-4 h-4 text-blue-500" />
              <span>Edit tool details</span>
            </div>
            <div className="flex items-center gap-2">
              <Trash2 className="w-4 h-4 text-red-500" />
              <span>Delete tool</span>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {editingTool && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-background border border-white/10 rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Edit Tool: {editingTool.name}</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-muted-foreground mb-1">Tool Name</label>
                <input 
                  type="text" 
                  value={editingTool.name}
                  onChange={(e) => setEditingTool({ ...editingTool, name: e.target.value })}
                  className="w-full px-4 py-3 bg-white/10 border border-white/10 rounded-xl" 
                />
              </div>
              <div>
                <label className="block text-sm text-muted-foreground mb-1">Description</label>
                <textarea
                  value={editingTool.description}
                  onChange={(e) => setEditingTool({ ...editingTool, description: e.target.value })}
                  className="w-full px-4 py-3 bg-white/10 border border-white/10 rounded-xl"
                  rows={2}
                />
              </div>
              <div>
                <label className="block text-sm text-muted-foreground mb-1">Path (URL)</label>
                <input 
                  type="text" 
                  value={editingTool.path}
                  onChange={(e) => setEditingTool({ ...editingTool, path: e.target.value })}
                  className="w-full px-4 py-3 bg-white/10 border border-white/10 rounded-xl" 
                />
              </div>
              <div>
                <label className="block text-sm text-muted-foreground mb-1">Daily Usage Limit (0 = unlimited)</label>
                <input 
                  type="number" 
                  value={editingTool.daily_limit}
                  onChange={(e) => setEditingTool({ ...editingTool, daily_limit: parseInt(e.target.value) || 0 })}
                  className="w-full px-4 py-3 bg-white/10 border border-white/10 rounded-xl" 
                />
              </div>
              <div>
                <label className="block text-sm text-muted-foreground mb-1">Icon</label>
                <select
                  value={editingTool.icon}
                  onChange={(e) => setEditingTool({ ...editingTool, icon: e.target.value })}
                  className="w-full px-4 py-3 bg-white/10 border border-white/10 rounded-xl"
                >
                  {Object.keys(iconMap).map(icon => (
                    <option key={icon} value={icon}>{icon}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={editingTool.is_enabled}
                    onChange={(e) => setEditingTool({ ...editingTool, is_enabled: e.target.checked })} 
                  />
                  Enabled
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input 
                    type="checkbox" 
                    checked={editingTool.is_premium}
                    onChange={(e) => setEditingTool({ ...editingTool, is_premium: e.target.checked })} 
                  />
                  Premium
                </label>
              </div>
              <div className="flex gap-3 pt-2">
                <button 
                  onClick={() => setEditingTool(null)}
                  className="flex-1 py-3 bg-white/10 rounded-xl font-semibold"
                >
                  Cancel
                </button>
                <button 
                  onClick={() => saveTool(editingTool)} 
                  className="flex-1 py-3 bg-cyan-500 text-white rounded-xl font-semibold flex items-center justify-center gap-2"
                >
                  <Save className="w-4 h-4" /> Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Tool Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-background border border-white/10 rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Add New Tool</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-muted-foreground mb-1">Tool ID (unique, lowercase)</label>
                <input 
                  type="text" 
                  value={newTool.id}
                  onChange={(e) => setNewTool({ ...newTool, id: e.target.value.toLowerCase().replace(/\s/g, '-') })}
                  placeholder="e.g., logo-maker"
                  className="w-full px-4 py-3 bg-white/10 border border-white/10 rounded-xl" 
                />
              </div>
              <div>
                <label className="block text-sm text-muted-foreground mb-1">Tool Name</label>
                <input 
                  type="text" 
                  value={newTool.name}
                  onChange={(e) => setNewTool({ ...newTool, name: e.target.value })}
                  placeholder="e.g., Logo Maker"
                  className="w-full px-4 py-3 bg-white/10 border border-white/10 rounded-xl" 
                />
              </div>
              <div>
                <label className="block text-sm text-muted-foreground mb-1">Description</label>
                <textarea
                  value={newTool.description}
                  onChange={(e) => setNewTool({ ...newTool, description: e.target.value })}
                  placeholder="What does this tool do?"
                  className="w-full px-4 py-3 bg-white/10 border border-white/10 rounded-xl"
                  rows={2}
                />
              </div>
              <div>
                <label className="block text-sm text-muted-foreground mb-1">Path (URL)</label>
                <input 
                  type="text" 
                  value={newTool.path}
                  onChange={(e) => setNewTool({ ...newTool, path: e.target.value })}
                  placeholder="e.g., /tools/logo-maker"
                  className="w-full px-4 py-3 bg-white/10 border border-white/10 rounded-xl" 
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button 
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 py-3 bg-white/10 rounded-xl font-semibold"
                >
                  Cancel
                </button>
                <button 
                  onClick={addTool} 
                  className="flex-1 py-3 bg-cyan-500 text-white rounded-xl font-semibold flex items-center justify-center gap-2"
                >
                  <Plus className="w-4 h-4" /> Add Tool
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FreeToolsAdmin;
